import React, { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Button, Spinner, Form, Nav } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import AccountSwitcher from '../components/common/AccountSwitcher';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import { usePerformanceBoard } from '../hooks/useListingPerformance';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import { TEXT, errorText } from '../config/listingPerformanceLabels';
import AirbnbBoard from '../components/listingPerformance/AirbnbBoard';
import GlanceStrip, { glanceFilter, RESULTS_DUE } from '../components/listingPerformance/GlanceStrip';
import { ThresholdChanges } from '../components/listingPerformance/parts';
import BookingcomSection from '../components/listingPerformance/BookingcomSection';
import AllPlatformsTable from '../components/listingPerformance/AllPlatformsTable';
import UnitDetail from '../components/listingPerformance/UnitDetail';
import PerformanceSettings from '../components/listingPerformance/PerformanceSettings';
import '../styles/ListingPerformance.css';

/**
 * «أداء الوحدات» — the weekly listing-performance record (tasks/property-performance-phase1.md step 5).
 * The PMS home of what was the Google Sheet «متابعة التغييرات وأداء الوحدات»: one row per unit per week,
 * the sheet's verdicts, the unit's detail and change log. Core staff (admin / ops / ops-manager).
 *
 * Every figure, verdict and colour is the server's (pageService → board.js — the same code the 11 Oct
 * reproduction test runs). The page picks the owner, the week, the tab and the view, and draws.
 *
 * The open tab and the week live in the URL (`?tab=bookingcom&week=2026-10-11`) so a link to a
 * particular read can be shared. «عرض المالك» asks the server for the owner cut — numbers and trends
 * only — and is never a client-side hide.
 *
 * Arabic-only, like every ops page.
 */
const TABS = ['airbnb', 'bookingcom', 'all', 'settings'];

const LastCollected = ({ last }) => (
  <p className="lp-last" role="status">
    <i className="bi bi-cloud-arrow-down" aria-hidden="true" />{' '}
    {last ? (
      <>
        {TEXT.lastCollected}: {formatDateTime(last.receivedAt)} — {TEXT.by} {last.sender || '—'}
        {last.summary && <span className="lp-last__summary"> — {last.summary}</span>}
      </>
    ) : TEXT.lastCollectedNone}
  </p>
);

const ListingPerformancePage = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const { selectedAccount } = useAccount();
  const [activeNavItem, setActiveNavItem] = useState('unit-performance');
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const tab = TABS.includes(tabParam) ? tabParam : 'airbnb';
  const week = searchParams.get('week') || undefined;
  const [ownerView, setOwnerView] = useState(false);
  // A picked unit remembers the owner it was picked under and is simply not applied under another
  // (the AvailabilityGridPage building-pick pattern) — no reset effect, no stale reopen.
  const [pick, setPick] = useState({ account: selectedAccount, unit: null });
  const selected = pick.account === selectedAccount ? pick.unit : null;
  const setSelected = (unit) => setPick({ account: selectedAccount, unit });
  // The glance filter: one verdict at a time. A verdict no row carries any more simply shows every row.
  const [verdictFilter, setVerdictFilter] = useState(null);

  // The owner view needs an owner. Without one NOTHING is asked for or drawn — never the staff board
  // under an owner-view banner.
  const ownerViewBlocked = ownerView && !selectedAccount;
  const view = ownerView ? 'owner' : 'staff';
  const board = usePerformanceBoard({ ownerId: selectedAccount, week, view }, tab !== 'settings' && !ownerViewBlocked);
  const data = board.data;
  const stale = board.isPlaceholderData;

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const pickWeek = (value) => { setParam('week', value); };
  const pickTab = (value) => { setParam('tab', value === 'airbnb' ? null : value); };

  // A selected unit belongs to the owner it was picked under — not shown once the owner changes.
  const unitInView = selected && data && [...(data.airbnb || []), ...(data.all || [])].some(r => r.unitCode === selected) ? selected : null;
  // The week the tab shows: the picked one, or the tab's own latest (Booking.com labels its own week).
  const shownWeek = data ? (data.week || (tab === 'all' ? data.tableWeeks?.all : tab === 'airbnb' ? data.tableWeeks?.airbnb : null)) : null;
  const filtered = (data?.airbnb || []).filter(glanceFilter(verdictFilter));
  const airbnbRows = view === 'staff' && verdictFilter && filtered.length ? filtered : (data?.airbnb || []);

  return (
    <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem} headerTitle={TEXT.pageTitle}>
      <Container fluid className="lp-page" dir="rtl">
        <div className="lp-switcher"><AccountSwitcher /></div>

        <header className="lp-head">
          <div>
            <h2 className="lp-title">{TEXT.pageTitle}</h2>
            <p className="lp-subtitle">{TEXT.pageSubtitle}</p>
          </div>
          {view === 'staff' && tab !== 'settings' && data && <LastCollected last={data.lastCollected} />}
        </header>

        <div className="lp-toolbar">
          <Nav variant="tabs" activeKey={tab} onSelect={pickTab} className="lp-tabs" role="tablist">
            {TABS.filter(t => t !== 'settings' || !ownerView).map(t => (
              <Nav.Item key={t}>
                <Nav.Link eventKey={t} className="touch-tab" role="tab" aria-selected={tab === t}>{TEXT.tabs[t]}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          {tab !== 'settings' && (
            <div className="lp-toolbar__controls">
              <Form.Group controlId="lp-week" className="lp-week">
                <Form.Label>{TEXT.weekLabel}</Form.Label>
                <Form.Select value={week || ''} onChange={(e) => pickWeek(e.target.value)} disabled={!week && !data?.weeks?.length}>
                  <option value="">{TEXT.latestWeek}</option>
                  {(data?.weeks || []).map(w => <option key={w} value={w}>{formatDate(w)}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="lp-owner-view"
                className="lp-owner-switch"
                label={TEXT.ownerView}
                checked={ownerView}
                onChange={(e) => setOwnerView(e.target.checked)}
              />
              <Button variant="link" className="lp-link" onClick={() => board.refetch()} disabled={board.isFetching}>
                <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {TEXT.refresh}
              </Button>
            </div>
          )}
        </div>

        {ownerView && tab !== 'settings' && (
          <p className="lp-banner" role="status">{selectedAccount ? TEXT.ownerViewHint : TEXT.ownerViewNeedsOwner}</p>
        )}

        {tab === 'settings' && <PerformanceSettings isAdmin={isAdmin} />}

        {tab !== 'settings' && (
          <>
            {board.isPending && !board.isError && !ownerViewBlocked && <div className="lp-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>}
            {board.isError && !ownerViewBlocked && (
              <div className="lp-error" role="alert">
                {errorText(board.error)}{' '}
                {week
                  ? <Button variant="link" className="lp-link" onClick={() => pickWeek(null)}>{TEXT.backToLatest}</Button>
                  : <Button variant="link" className="lp-link" onClick={() => board.refetch()}>{TEXT.refresh}</Button>}
              </div>
            )}
            {data && !ownerViewBlocked && !data.weeks?.length && <p className="lp-empty">{TEXT.noWeeks}</p>}

            {data && !ownerViewBlocked && data.weeks?.length > 0 && (
              <div className={`lp-body${stale ? ' lp-stale' : ''}`} aria-busy={stale}>
                <p className="lp-level">
                  {TEXT.tabLevel[tab]}
                  {shownWeek && <> — {TEXT.weekLabel} {formatDate(shownWeek)}</>}
                </p>
                {view === 'staff' && data.pendingThresholds && (
                  <p className="lp-banner lp-banner--pending" role="status">
                    <i className="bi bi-sliders" aria-hidden="true" /> {TEXT.pendingLead} {formatDate(data.pendingThresholds.appliesFromWeekEnd)}:{' '}
                    <ThresholdChanges changes={data.pendingThresholds.changes} />
                    . {TEXT.pendingTail}
                  </p>
                )}
                {tab === 'airbnb' && view === 'staff' && (
                  <GlanceStrip rows={data.airbnb} active={verdictFilter && filtered.length ? verdictFilter : null} onPick={setVerdictFilter} />
                )}
                {tab === 'airbnb' && (
                  <AirbnbBoard rows={airbnbRows} ownerView={view === 'owner'} marketShown={view === 'staff' && Boolean(data?.marketPrice?.enabled)} selected={unitInView} onSelect={setSelected} />
                )}
                {tab === 'bookingcom' && <BookingcomSection sections={data.bookingcom} ownerView={view === 'owner'} />}
                {tab === 'all' && (
                  <AllPlatformsTable rows={data.all} ownerView={view === 'owner'} selected={unitInView} onSelect={setSelected} />
                )}
                {unitInView && tab !== 'bookingcom' && (
                  <UnitDetail
                    unitCode={unitInView}
                    week={week || data.tableWeeks?.[tab === 'all' ? 'all' : 'airbnb'] || undefined}
                    ownerId={selectedAccount}
                    view={view}
                    focusDue={tab === 'airbnb' && view === 'staff' && verdictFilter === RESULTS_DUE}
                    onClose={() => setSelected(null)}
                  />
                )}
              </div>
            )}
          </>
        )}
      </Container>
    </DesktopLayout>
  );
};

export default ListingPerformancePage;
